/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext } from 'react';
import { Form, Button, Stack } from 'react-bootstrap';
import { getSetting, setSetting } from '../../data';
import { MetadataContext } from '../MetadataContext';
import { SettingsContext } from '../SettingsContext';
import { useTheme } from 'styled-components';

function DeveloperSettings() {
    const theme = useTheme() as any;
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const metadataContext = useContext<any>(MetadataContext);
    const settingsContext = useContext(SettingsContext);
    const [refresh, setRefresh] = useState(0);

    const [gitUserName,    setGitUserName]    = useState('');
    const [gitEmail,       setGitEmail]       = useState('');
    const [sshKey,         setSshKey]         = useState('');
    const [sshKnownHosts,  setSshKnownHosts]  = useState('');

    const [origGitUserName,   setOrigGitUserName]   = useState('');
    const [origGitEmail,      setOrigGitEmail]      = useState('');
    const [origSshKey,        setOrigSshKey]        = useState('');
    const [origSshKnownHosts, setOrigSshKnownHosts] = useState('');

    const dirty = gitUserName !== origGitUserName
        || gitEmail !== origGitEmail
        || sshKey !== origSshKey
        || sshKnownHosts !== origSshKnownHosts;

    useEffect(() => { settingsContext.setIsDirty(dirty); }, [dirty, settingsContext]);

    useEffect(() => {
        async function load() {
            try {
                const name  = await getSetting('/dev/git_user_name');
                const email = await getSetting('/dev/git_email');
                const key   = await getSetting('/dev/ssh_key');
                const hosts = await getSetting('/dev/ssh_known_hosts');
                setGitUserName(name || '');    setOrigGitUserName(name || '');
                setGitEmail(email || '');      setOrigGitEmail(email || '');
                setSshKey(key || '');          setOrigSshKey(key || '');
                setSshKnownHosts(hosts || ''); setOrigSshKnownHosts(hosts || '');
            } catch (err: any) {
                console.error('Error loading developer settings:', err);
            }
        }
        load();
    }, [refresh]);

    async function save() {
        await setSetting('/dev/git_user_name',   gitUserName.trim(),   'Git commit author name');
        await setSetting('/dev/git_email',        gitEmail.trim(),      'Git commit author email');
        await setSetting('/dev/ssh_key',          sshKey.trim(),        'SSH private key for git push (literal PEM content)');
        await setSetting('/dev/ssh_known_hosts',  sshKnownHosts.trim(), 'SSH known hosts override');
        metadataContext.refresh();
        setRefresh(r => r + 1);
    }

    const changedStyle = (changed: boolean): React.CSSProperties => changed ? {
        borderLeft: `3px solid ${theme?.colors?.info || '#0dcaf0'}`,
        paddingLeft: '0.75rem',
    } : {};

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-4"
                style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <div>
                    <h3 className="mb-1">Developer</h3>
                    <div className="text-muted">Git author identity and SSH configuration for git push.</div>
                </div>
                {dirty && (
                    <Stack direction="horizontal" gap={2}>
                        <Button variant="outline-secondary" onClick={() => {
                            setGitUserName(origGitUserName);
                            setGitEmail(origGitEmail);
                            setSshKey(origSshKey);
                            setSshKnownHosts(origSshKnownHosts);
                        }}>Clear</Button>
                        <Button variant="primary" onClick={save}>Save</Button>
                    </Stack>
                )}
            </div>

            <div style={{ borderTop: sectionBorder, paddingTop: '1rem', marginTop: '1rem' }}>
                <h5 className="mb-3">Git Author</h5>

                <div className="mb-4" style={changedStyle(gitUserName !== origGitUserName)}>
                    <Form.Label htmlFor="gitUserName" className="fw-semibold">Name</Form.Label>
                    <div className="text-muted small mb-2">Author name used for git commits. Defaults to <code>FBot</code> if empty.</div>
                    <Form.Control
                        id="gitUserName"
                        value={gitUserName}
                        onChange={e => setGitUserName(e.target.value)}
                        type="text"
                        placeholder="FBot"
                        style={{ maxWidth: '28rem' }}
                    />
                </div>

                <div className="mb-4" style={changedStyle(gitEmail !== origGitEmail)}>
                    <Form.Label htmlFor="gitEmail" className="fw-semibold">Email</Form.Label>
                    <div className="text-muted small mb-2">Author email used for git commits. Defaults to <code>fbot@ipivot.ai</code> if empty.</div>
                    <Form.Control
                        id="gitEmail"
                        value={gitEmail}
                        onChange={e => setGitEmail(e.target.value)}
                        type="email"
                        placeholder="fbot@ipivot.ai"
                        style={{ maxWidth: '28rem' }}
                    />
                </div>
            </div>

            <div style={{ borderTop: sectionBorder, paddingTop: '1rem', marginTop: '1rem' }}>
                <h5 className="mb-3">SSH</h5>

                <div className="mb-4" style={changedStyle(sshKey !== origSshKey)}>
                    <Form.Label htmlFor="sshKey" className="fw-semibold">Private Key</Form.Label>
                    <div className="text-muted small mb-2">
                        PEM-format private key content for git push over SSH.
                        Leave empty to use the system default (<code>~/.ssh/id_rsa</code> etc.).
                    </div>
                    <Form.Control
                        id="sshKey"
                        as="textarea"
                        rows={6}
                        value={sshKey}
                        onChange={e => setSshKey(e.target.value)}
                        style={{ fontFamily: 'monospace', fontSize: '0.8rem', maxWidth: '44rem' }}
                        placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                        spellCheck={false}
                    />
                </div>

                <div className="mb-4" style={changedStyle(sshKnownHosts !== origSshKnownHosts)}>
                    <Form.Label htmlFor="sshKnownHosts" className="fw-semibold">Known Hosts</Form.Label>
                    <div className="text-muted small mb-2">
                        Override known_hosts entries. If empty, git will use <code>StrictHostKeyChecking=accept-new</code>.
                    </div>
                    <Form.Control
                        id="sshKnownHosts"
                        as="textarea"
                        rows={4}
                        value={sshKnownHosts}
                        onChange={e => setSshKnownHosts(e.target.value)}
                        style={{ fontFamily: 'monospace', fontSize: '0.8rem', maxWidth: '44rem' }}
                        placeholder="github.com ssh-ed25519 AAAA..."
                        spellCheck={false}
                    />
                </div>
            </div>
        </div>
    );
}

export default DeveloperSettings;
